import React, { useEffect, useRef, useState } from "react";
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
} from "firebase/auth";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  TextField,
  Typography,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import MailIcon from "@mui/icons-material/MailOutline";
import { auth } from "../firebase";
import { signOutUser, useSession } from "../auth/AuthProvider";
import { Brand } from "../components/AppShell";

const EMAIL_KEY = "keyportal.emailForSignIn";

const storage = {
  get: () => {
    try {
      return window.localStorage.getItem(EMAIL_KEY);
    } catch {
      return null;
    }
  },
  set: (value) => {
    try {
      window.localStorage.setItem(EMAIL_KEY, value);
    } catch {
      /* private mode: user will be asked for the email again */
    }
  },
  clear: () => {
    try {
      window.localStorage.removeItem(EMAIL_KEY);
    } catch {
      /* ignore */
    }
  },
};

function AuthCard({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        py: 6,
        background: "linear-gradient(180deg, #FAFAF8 0%, #F5EDE0 100%)",
      }}
    >
      <Card sx={{ width: "100%", maxWidth: 420, borderRadius: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ mb: 3 }}>
            <Brand subtitle="LiFE licence keys" />
          </Box>
          {children}
        </CardContent>
      </Card>
    </Box>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null); // { severity, text }
  const [finishingLink, setFinishingLink] = useState(false);
  const linkHandled = useRef(false);

  // Coming back from the email link. The code is single-use, so make sure it
  // is only redeemed once (effects run twice in development).
  useEffect(() => {
    if (
      linkHandled.current ||
      !isSignInWithEmailLink(auth, window.location.href)
    )
      return;
    linkHandled.current = true;
    const saved = storage.get();
    if (saved) {
      completeEmailLink(saved);
    } else {
      setFinishingLink(true);
      setMessage({
        severity: "info",
        text: "Confirm your email address to finish signing in.",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function completeEmailLink(address) {
    setBusy(true);
    try {
      await signInWithEmailLink(auth, address.trim(), window.location.href);
      storage.clear();
      window.history.replaceState(null, "", "/");
    } catch (error) {
      console.error(error);
      setMessage({
        severity: "error",
        text: "This sign-in link is invalid or has expired. Request a new one below.",
      });
      setFinishingLink(false);
    } finally {
      setBusy(false);
    }
  }

  async function signInWithGoogle() {
    setMessage(null);
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (error) {
      if (error.code !== "auth/popup-closed-by-user") {
        console.error(error);
        setMessage({
          severity: "error",
          text: "Google sign-in failed. Please try again.",
        });
      }
    }
  }

  async function sendLink(event) {
    event.preventDefault();
    const address = email.trim();
    if (!address) return;
    if (finishingLink) {
      completeEmailLink(address);
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      await sendSignInLinkToEmail(auth, address, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true,
      });
      storage.set(address);
      setMessage({
        severity: "success",
        text: `We sent a sign-in link to ${address}. Open it on this device to continue.`,
      });
    } catch (error) {
      console.error(error);
      setMessage({
        severity: "error",
        text: "Couldn't send the sign-in link. Check the address and try again.",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthCard>
      <Typography variant="h4" component="h1" gutterBottom>
        Sign in
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        For LiFE staff and authorised distributors.
      </Typography>

      {message && (
        <Alert severity={message.severity} sx={{ mb: 2 }}>
          {message.text}
        </Alert>
      )}

      {!finishingLink && (
        <>
          <Button
            fullWidth
            size="large"
            variant="contained"
            startIcon={<GoogleIcon />}
            onClick={signInWithGoogle}
          >
            Continue with Google
          </Button>
          <Divider sx={{ my: 3, color: "text.secondary", fontSize: 13 }}>
            or get a sign-in link
          </Divider>
        </>
      )}

      <Box
        component="form"
        onSubmit={sendLink}
        sx={{ display: "grid", gap: 1.5 }}
      >
        <TextField
          type="email"
          label="Work email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
          fullWidth
        />
        <Button
          type="submit"
          variant="outlined"
          color="inherit"
          size="large"
          startIcon={<MailIcon />}
          disabled={busy}
          sx={{ borderColor: "divider" }}
        >
          {finishingLink
            ? "Finish signing in"
            : busy
              ? "Sending…"
              : "Email me a link"}
        </Button>
      </Box>
    </AuthCard>
  );
}

export function NoAccessPage() {
  const session = useSession();
  const unverified = session.reason === "unverified";
  return (
    <AuthCard>
      <Typography variant="h4" component="h1" gutterBottom>
        {session.status === "error" ? "Something went wrong" : "No access yet"}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {session.status === "error"
          ? "We couldn't load your portal access. Check your connection and reload the page."
          : unverified
            ? "Your email address hasn't been verified. Sign in with Google or with an email link instead."
            : `${session.email || "This account"} isn't registered for the LiFE KeyPortal. If you're a distributor, ask your LiFE contact to add this email address.`}
      </Typography>
      <Button
        variant="outlined"
        color="inherit"
        onClick={signOutUser}
        fullWidth
      >
        Sign out and use another account
      </Button>
    </AuthCard>
  );
}
